import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client/react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { FormField } from '@/components/ui/FormLayout';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { OverviewPanel } from '@/components/ui/OverviewPanel';
import { Select } from '@/components/ui/Select';
import { graphqlErrorMessage } from '@/lib/graphqlError';
import { formatCurrency, formatDate, formatDateTime, formatShortId } from '@/lib/format';
import { useBackHref } from '@/hooks/useListLocation';
import {
  entranceKindLabel,
  entranceResultLabel,
  entranceResultTone,
  refuseReasonLabel,
  ticketBlockReasonLabel,
  ticketEmailChangeActorLabel,
  ticketKindLabel,
  ticketStatusLabel,
  ticketStatusTone,
} from '@/lib/labels';
import { Can } from '@/lib/permissions';
import type { ManageLinkFor, TicketBlockReason } from '@/gql/graphql';
import { ChangeCompanionEmailModal } from './AdminTicketsGrid';
import {
  AdminTicketDoc,
  BLOCK_TICKET_CONSEQUENCE,
  BlockTicketDoc,
  canBlockTicket,
  canChangeCompanionEmail,
  CreateManageTicketsLinkDoc,
  RELEASE_HELD_TICKET_CONSEQUENCE,
  ReleaseHeldTicketDoc,
  ticketSaleChannelLabel,
  UNBLOCK_TICKET_CONSEQUENCE,
  UnblockTicketDoc,
} from './adminTickets';

function Row({
  label,
  children,
  valueId,
}: {
  label: string;
  children: ReactNode;
  valueId?: string;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd id={valueId} className="text-right text-gray-900 dark:text-white">
        {children}
      </dd>
    </div>
  );
}

export function TicketDetailPage() {
  const backHref = useBackHref('/tickets');
  const { id = '' } = useParams();
  const { data, previousData, loading, error, refetch } = useQuery(AdminTicketDoc, {
    variables: { id },
  });
  const [emailOpen, setEmailOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);
  const ticket = (data ?? previousData)?.adminTicket;

  if (loading && !ticket) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load ticket" description={error.message} />;
  }
  if (!ticket) {
    return (
      <EmptyState
        title="Ticket not found"
        description="This ticket does not exist in this venue."
      />
    );
  }

  const claim = ticket.claim;
  // A Stager-blocked ticket is BLOCKED but still carries its barcode.
  const isImported = ticket.status === 'IMPORTED' || Boolean(ticket.externalCode);
  const guestName = claim
    ? [claim.firstName, claim.lastName].filter(Boolean).join(' ').trim()
    : '';

  return (
    <div className="space-y-4" id="admin.tickets.detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={backHref} id="admin.tickets.back">
            <Button variant="outline" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Back to tickets
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Ticket #{formatShortId(ticket.id)}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Created {formatDateTime(ticket.createdAt)} · {ticket.nightTitle} ·{' '}
              {ticket.stageName}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={ticketStatusTone(ticket.status)}>
            {ticketStatusLabel(ticket.status)}
          </Badge>
          <Badge tone="neutral">{ticketKindLabel(ticket.kind)}</Badge>
          {ticket.status === 'HELD' ? (
            <Can permission="ticket:manage">
              <Button
                id="admin.tickets.release"
                variant="outline"
                size="sm"
                onClick={() => setReleaseOpen(true)}
              >
                Release
              </Button>
            </Can>
          ) : null}
          {canBlockTicket(ticket) ? (
            <Can permission="ticket:manage">
              <Button
                id="admin.tickets.block"
                variant="outline"
                size="sm"
                onClick={() => setBlockOpen(true)}
              >
                Block
              </Button>
            </Can>
          ) : null}
          {ticket.status === 'BLOCKED' ? (
            <Can permission="ticket:manage">
              <Button
                id="admin.tickets.unblock"
                variant="outline"
                size="sm"
                onClick={() => setUnblockOpen(true)}
              >
                Unblock
              </Button>
            </Can>
          ) : null}
          {canChangeCompanionEmail(ticket) ? (
            <Can permission="ticket:manage">
              <Button
                id="admin.tickets.changeEmail"
                variant="outline"
                size="sm"
                onClick={() => setEmailOpen(true)}
              >
                Change email
              </Button>
            </Can>
          ) : null}
        </div>
      </div>

      <OverviewPanel
        stats={[
          {
            id: 'admission',
            label: 'Admission',
            value: formatCurrency(ticket.ticketPrice),
          },
          {
            id: 'fee',
            label: 'Booking fee',
            value: formatCurrency(ticket.feePrice),
          },
          {
            id: 'buyer',
            label: 'Buyer',
            value: ticket.buyerEmail || '—',
          },
          {
            id: 'guest',
            label: 'Claimed as',
            value: guestName || '—',
          },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Ticket" className="lg:col-span-1">
          <dl className="space-y-2 text-sm">
            <Row label="Night">
              <Link
                to={`/nights/${ticket.nightId}`}
                className="text-brand hover:underline"
              >
                {ticket.nightTitle}
              </Link>
            </Row>
            <Row label="Phase">{ticket.stageName}</Row>
            <Row label="Type">{ticketKindLabel(ticket.kind)}</Row>
            <Row label="Channel" valueId="admin.tickets.detail.channel">
              {ticketSaleChannelLabel(ticket.saleChannel)}
            </Row>
            {ticket.externalCode ? (
              <Row label="Imported from Stager" valueId="admin.tickets.externalCode">
                {ticket.externalCode}
              </Row>
            ) : null}
            <Row label="Status">{ticketStatusLabel(ticket.status)}</Row>
            <Row label="Recognition date">{formatDate(ticket.recognitionDate)}</Row>
            <Row label="Purchased">{formatDateTime(ticket.purchasedAt)}</Row>
            <Row label="Claimed">{formatDateTime(ticket.claimedAt)}</Row>
            <Row label="Hold expires">{formatDateTime(ticket.holdExpiresAt)}</Row>
            <Row label="Released">{formatDateTime(ticket.releasedAt)}</Row>
            <Row label="Refunded">{formatDateTime(ticket.refundedAt)}</Row>
            <Row label="Updated">{formatDateTime(ticket.updatedAt)}</Row>
          </dl>
        </Card>

        <Card title="Related" className="lg:col-span-1">
          <dl className="space-y-2 text-sm">
            <Row label="Order">
              {ticket.orderId ? (
                <Link
                  to={`/orders/${ticket.orderId}`}
                  className="text-brand hover:underline"
                >
                  #{formatShortId(ticket.orderId)}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="User">
              {ticket.userId ? (
                <Link
                  to={`/users/${ticket.userId}`}
                  className="text-brand hover:underline"
                >
                  #{formatShortId(ticket.userId)}
                </Link>
              ) : (
                '—'
              )}
            </Row>
            <Row label="Buyer e-mail" valueId="admin.tickets.detail.buyerEmail">
              {ticket.buyerEmail || '—'}
              {ticket.buyerEmail ? (
                <ManageTicketsLink ticketId={ticket.id} who="BUYER" />
              ) : null}
            </Row>
            <Row label="Claimer e-mail" valueId="admin.tickets.detail.claimerEmail">
              {ticket.claimerEmail || '—'}
              {ticket.claimerEmail ? (
                <ManageTicketsLink ticketId={ticket.id} who="CLAIMER" />
              ) : null}
            </Row>
          </dl>
          <div className="mt-4 space-y-2">
            <p className="text-xs font-semibold tracking-wider text-gray-500 uppercase dark:text-gray-400">
              Payments
            </p>
            {ticket.payments.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No payments on this order.</p>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {ticket.payments.map((p) => (
                  <li key={p.id} className="flex justify-between gap-3">
                    <Link
                      to={`/payments?search=${encodeURIComponent(p.id)}`}
                      className="text-brand hover:underline"
                    >
                      #{formatShortId(p.id)}
                    </Link>
                    <span className="text-gray-700 dark:text-gray-200">
                      {p.paymentMethod} · {p.status} · {formatCurrency(p.amount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card title="Claim" className="lg:col-span-1">
          {claim ? (
            <dl className="space-y-2 text-sm">
              <Row label="Name">
                {claim.firstName} {claim.lastName}
              </Row>
              <Row label="Claimed by" valueId="admin.tickets.detail.claimedBy">
                {ticket.holderEmail}
              </Row>
              <Row label="Date of birth">{claim.dateOfBirth}</Row>
              <Row label="Phone">{claim.phone || '—'}</Row>
              <Row label="City">{claim.city || '—'}</Row>
              <Row label="Country">{claim.country || '—'}</Row>
              <Row label="Attested">{claim.attested ? 'Yes' : 'No'}</Row>
              <Row label="Claimed at">{formatDateTime(claim.claimedAt)}</Row>
              <Row label="User">
                {claim.userId ? (
                  <Link
                    to={`/users/${claim.userId}`}
                    className="text-brand hover:underline"
                  >
                    #{formatShortId(claim.userId)}
                  </Link>
                ) : (
                  '—'
                )}
              </Row>
            </dl>
          ) : (
            <EmptyState
              title={isImported ? 'Imported' : 'Not claimed'}
              description={
                isImported
                  ? 'Imported from Stager; no claim in Ferry.'
                  : 'No claim details yet. Door tickets are never claimed.'
              }
            />
          )}
        </Card>
      </div>

      <Card title="Entrance">
        {ticket.entranceLogs.length === 0 ? (
          <EmptyState
            title="No scans"
            description="This ticket has not been presented at the door."
          />
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs tracking-wide text-gray-500 uppercase">
              <tr>
                <th className="px-2 py-2">Time</th>
                <th className="px-2 py-2">Type</th>
                <th className="px-2 py-2">Result</th>
                <th className="px-2 py-2">Guest</th>
                <th className="px-2 py-2">Scanner</th>
                <th className="px-2 py-2">Note</th>
              </tr>
            </thead>
            <tbody>
              {ticket.entranceLogs.map((log) => (
                <tr key={log.id} className="border-t border-gray-100 dark:border-white/10">
                  <td className="px-2 py-2">{formatDateTime(log.scannedAt)}</td>
                  <td className="px-2 py-2">{entranceKindLabel(log.kind)}</td>
                  <td className="px-2 py-2">
                    <Badge tone={entranceResultTone(log.result)}>
                      {entranceResultLabel(log.result)}
                    </Badge>
                  </td>
                  <td className="px-2 py-2">{log.guestName || '—'}</td>
                  <td className="px-2 py-2">{log.scannerName || '—'}</td>
                  <td className="px-2 py-2">
                    {log.refuseReason
                      ? refuseReasonLabel(log.refuseReason)
                      : ''}
                    {log.refuseNote ? ` — ${log.refuseNote}` : log.refuseReason ? '' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Blocks">
        {ticket.blocks.length === 0 ? (
          <EmptyState
            title="Never blocked"
            description="This ticket has not been blocked."
          />
        ) : (
          <ul className="space-y-3 text-sm" id="admin.tickets.blocks">
            {ticket.blocks.map((block) => {
              const open = !block.unblockedAt;
              return (
                <li
                  key={block.id}
                  id={`admin.tickets.block.${block.id}`}
                  className={
                    open
                      ? 'rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-500/30 dark:bg-red-500/10'
                      : 'px-3'
                  }
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={open ? 'error' : 'neutral'}>
                      {ticketBlockReasonLabel(block.reason)}
                    </Badge>
                    <span className="text-gray-700 dark:text-gray-200">
                      {open
                        ? `Blocked since ${formatDateTime(block.blockedAt)}`
                        : `Blocked ${formatDateTime(block.blockedAt)}, unblocked ${formatDateTime(block.unblockedAt)}`}
                    </span>
                  </div>
                  {block.note ? (
                    <p className="mt-1 text-gray-700 dark:text-gray-200">{block.note}</p>
                  ) : null}
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Blocked
                    {block.blockedByUserId ? (
                      <>
                        {' by '}
                        <Link
                          to={`/users/${block.blockedByUserId}`}
                          className="text-brand hover:underline"
                        >
                          #{formatShortId(block.blockedByUserId)}
                        </Link>
                      </>
                    ) : null}
                    {block.unblockedByUserId ? (
                      <>
                        {' · '}unblocked by{' '}
                        <Link
                          to={`/users/${block.unblockedByUserId}`}
                          className="text-brand hover:underline"
                        >
                          #{formatShortId(block.unblockedByUserId)}
                        </Link>
                      </>
                    ) : null}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card title="Email changes">
        {ticket.emailChanges.length === 0 ? (
          <EmptyState
            title="No email changes"
            description="This ticket's address has not been changed."
          />
        ) : (
          <ul className="space-y-1.5 text-sm">
            {ticket.emailChanges.map((change) => (
              <li key={change.id} id={`admin.tickets.emailChange.${change.id}`}>
                <span className="font-medium">
                  {ticketEmailChangeActorLabel(change.actorKind)}
                </span>
                {change.actorUserId ? (
                  <>
                    {' '}
                    (
                    <Link
                      to={`/users/${change.actorUserId}`}
                      className="text-brand hover:underline"
                    >
                      #{formatShortId(change.actorUserId)}
                    </Link>
                    )
                  </>
                ) : null}
                {': '}
                <span className="text-gray-500 dark:text-gray-400">
                  {change.previousEmail || '—'}
                </span>
                {' → '}
                <span>{change.newEmail}</span>
                <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                  {formatDateTime(change.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <ReleaseHeldTicketModal
        open={releaseOpen}
        ticketId={ticket.id}
        onClose={() => setReleaseOpen(false)}
        onReleased={async () => {
          setReleaseOpen(false);
          await refetch();
        }}
      />
      <BlockTicketModal
        key={blockOpen ? `block-${ticket.id}` : 'block-closed'}
        open={blockOpen}
        ticketId={ticket.id}
        onClose={() => setBlockOpen(false)}
        onBlocked={async () => {
          setBlockOpen(false);
          await refetch();
        }}
      />
      <UnblockTicketModal
        key={unblockOpen ? `unblock-${ticket.id}` : 'unblock-closed'}
        open={unblockOpen}
        ticketId={ticket.id}
        onClose={() => setUnblockOpen(false)}
        onUnblocked={async () => {
          setUnblockOpen(false);
          await refetch();
        }}
      />
      <ChangeCompanionEmailModal
        key={emailOpen ? ticket.id : 'closed'}
        ticket={emailOpen ? ticket : null}
        onClose={() => setEmailOpen(false)}
        onChanged={async () => {
          setEmailOpen(false);
          await refetch();
        }}
      />
    </div>
  );
}

/**
 * Mints the 90-day "manage my tickets" link for the buyer or the claimer
 * address and opens it in a new tab. The URL is never rendered; it only goes
 * to `window.open`.
 */
const MANAGE_LINK_ID_SUFFIX: Record<ManageLinkFor, string> = {
  BUYER: 'buyer',
  CLAIMER: 'claimer',
};

function ManageTicketsLink({ ticketId, who }: { ticketId: string; who: ManageLinkFor }) {
  const key = MANAGE_LINK_ID_SUFFIX[who];
  const [error, setError] = useState<string | null>(null);
  const [mint, { loading }] = useMutation(CreateManageTicketsLinkDoc);

  return (
    <Can permission="ticket:manage">
      <div>
        <button
          type="button"
          id={`admin.tickets.manageLink.${key}`}
          disabled={loading}
          onClick={async () => {
            setError(null);
            try {
              const res = await mint({ variables: { ticketId, who } });
              const url = res.data?.createManageTicketsLink;
              if (url) window.open(url, '_blank', 'noopener');
              else setError('No link was returned. Try again.');
            } catch (err) {
              setError(graphqlErrorMessage(err));
            }
          }}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-brand disabled:opacity-50 dark:text-gray-400"
        >
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          Manage tickets
        </button>
        {error ? (
          <p
            id="admin.tickets.manageLink.error"
            className="text-xs text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Can>
  );
}

function ReleaseHeldTicketModal({
  open,
  ticketId,
  onClose,
  onReleased,
}: {
  open: boolean;
  ticketId: string;
  onClose: () => void;
  onReleased: () => void | Promise<unknown>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [release, { loading }] = useMutation(ReleaseHeldTicketDoc);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Release ticket"
      description={RELEASE_HELD_TICKET_CONSEQUENCE}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            id="admin.tickets.releaseConfirm"
            loading={loading}
            onClick={async () => {
              setError(null);
              try {
                await release({ variables: { id: ticketId } });
                await onReleased();
              } catch (err) {
                setError(graphqlErrorMessage(err));
              }
            }}
          >
            Release
          </Button>
        </>
      }
    >
      {error ? <p className="text-sm text-red-600 dark:text-red-400">{error}</p> : null}
    </Modal>
  );
}

const BLOCK_REASON_OPTIONS: { value: TicketBlockReason; label: string }[] = (
  ['FRAUD', 'BANK_REFUND', 'WRONG_SALE', 'OTHER'] as const
).map((value) => ({ value, label: ticketBlockReasonLabel(value) }));

function BlockTicketModal({
  open,
  ticketId,
  onClose,
  onBlocked,
}: {
  open: boolean;
  ticketId: string;
  onClose: () => void;
  onBlocked: () => void | Promise<unknown>;
}) {
  const [reason, setReason] = useState<TicketBlockReason | ''>('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [block, { loading }] = useMutation(BlockTicketDoc);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Block ticket"
      description={BLOCK_TICKET_CONSEQUENCE}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            id="admin.tickets.blockConfirm"
            variant="danger"
            loading={loading}
            disabled={!reason}
            onClick={async () => {
              if (!reason) return;
              setError(null);
              try {
                await block({
                  variables: {
                    input: { id: ticketId, reason, note: note.trim() || undefined },
                  },
                });
                await onBlocked();
              } catch (err) {
                setError(graphqlErrorMessage(err));
              }
            }}
          >
            Block ticket
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <FormField label="Reason" required htmlFor="admin.tickets.blockReason">
          <Select
            id="admin.tickets.blockReason"
            placeholder="Choose a reason"
            options={BLOCK_REASON_OPTIONS}
            value={reason}
            onChange={(e) => setReason(e.target.value as TicketBlockReason)}
          />
        </FormField>
        <FormField label="Note" htmlFor="admin.tickets.blockNote">
          <Input
            id="admin.tickets.blockNote"
            autoComplete="off"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional"
          />
        </FormField>
        {error ? (
          <p id="admin.tickets.blockError" className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function UnblockTicketModal({
  open,
  ticketId,
  onClose,
  onUnblocked,
}: {
  open: boolean;
  ticketId: string;
  onClose: () => void;
  onUnblocked: () => void | Promise<unknown>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [unblock, { loading }] = useMutation(UnblockTicketDoc);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Unblock ticket"
      description={UNBLOCK_TICKET_CONSEQUENCE}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            id="admin.tickets.unblockConfirm"
            loading={loading}
            onClick={async () => {
              setError(null);
              try {
                await unblock({ variables: { id: ticketId } });
                await onUnblocked();
              } catch (err) {
                setError(graphqlErrorMessage(err));
              }
            }}
          >
            Unblock ticket
          </Button>
        </>
      }
    >
      {error ? (
        <p id="admin.tickets.unblockError" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </Modal>
  );
}
